import { scaleLinear } from "d3-scale";
import { select } from "d3-selection";
import React, { useCallback, useEffect, useRef } from "react";
import ImagerApi from "../../api/ImagerApi";
import { getHistogramData } from "../../redux/reducers/LiveImageReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import ImageLevelingSlider from "../Controls/ImageLevelingSlider";
import SpinnerTextBox from "./../Controls/SpinnerTextBox";
import MtiButton from "../Buttons/MtiButton";
import { getModuleStateById } from "../../redux/reducers/ModuleStateReducer";
import { LevellingState } from "../../Model/ModuleState";

type ButtonToggleProps = {
  disabled: boolean;
  name: string;
  onClick: () => void;
  checked: boolean;
};

const ButtonToggle = React.memo(({ disabled, onClick, name, checked }: ButtonToggleProps) => {
  return (
    <MtiButton
      size="large"
      onClick={onClick}
      disabled={disabled}
      style={{ height: "100%", minHeight: 32, background: checked ? "#D26300" : "#F7A246" }}
    >
      {name}
    </MtiButton>
  );
});

type ButtonInfoLevelProps = {
  value: {
    min: number;
    max: number;
  };
  onValuesChanged: (value: { min: number; max: number }) => void;
  autoLevel: boolean;
  disabled: boolean;
  style?: React.CSSProperties;
  onClickReset: () => void;
  onClickAuto: () => void;
};

function ButtonInfoLevel({
  value: values,
  onValuesChanged,
  autoLevel,
  disabled,
  onClickReset,
  onClickAuto,
  style,
}: ButtonInfoLevelProps) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        ...style,
      }}
    >
      <SpinnerTextBox
        disabled={disabled || autoLevel}
        value={values.min}
        onValueChanged={(val: number) =>
          onValuesChanged({
            min: val,
            max: values.max,
          })
        }
        step={1}
        min={0}
        max={values.max - 1}
        style={{
          width: 85,
          height: "100%",
        }}
      />
      <MtiButton size="large" onClick={onClickReset} disabled={disabled} style={{ height: "100%", minHeight: 32 }}>
        RESET
      </MtiButton>
      <ButtonToggle name={"AUTO"} onClick={onClickAuto} checked={autoLevel} disabled={false} />
      <SpinnerTextBox
        disabled={disabled || autoLevel}
        value={values.max}
        onValueChanged={(val: number) =>
          onValuesChanged({
            min: values.min,
            max: val,
          })
        }
        step={1}
        min={values.min + 1}
        max={255}
        style={{
          width: 85,
          height: "100%",
        }}
      />
    </div>
  );
}

type HistogramProps = {
  histogramValues: number[];
  boundary: { min: number; max: number };
  style?: React.CSSProperties;
};

function Histogram({ histogramValues, boundary, style }: HistogramProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  useEffect(() => {
    if (!histogramValues) {
      return;
    }

    if (svgRef.current == null) {
      return;
    }

    const svg = select<SVGSVGElement, SVGSVGElement>(svgRef.current);
    const maxDataValue = Math.max.apply(Math, histogramValues);
    const height = 100;
    if (maxDataValue !== 0) {
      const rectWidth = 100 / histogramValues.length;
      const hist2PixelScale = scaleLinear().domain([0, maxDataValue]).range([0, height]);

      const getHeight = (value: number, index: number): string => {
        if (index === boundary.min || index === boundary.max) {
          return "0";
        } else {
          let scalledValue = 0;
          if (hist2PixelScale(value) !== undefined) {
            scalledValue = hist2PixelScale(value) as number;
          }
          return height - scalledValue + "%";
        }
      };

      const barColor = (index: number): string => {
        if (index === boundary.min || index === boundary.max) {
          return "red";
        } else {
          return "black";
        }
      };

      svg
        .selectAll("rect")
        .data(histogramValues)
        .join("rect")
        .style("fill", (d, i) => barColor(i))
        .style("stroke", (d, i) => barColor(i))
        .style("strokeWidth", "0.07%")
        .attr("x", (d, i) => i * rectWidth + "%")
        .attr("y", (d, i) => getHeight(d, i))
        .attr("width", (d, i) => rectWidth + "%")
        .attr("height", height + "%");
    }
  }, [boundary, histogramValues, svgRef]);

  return (
    <svg
      ref={svgRef}
      style={{
        ...style,
      }}
    />
  );
}

type Props = {
  imagerIdentifier: string;
  style?: React.CSSProperties;
};

function isLevellingStateEqual(prev?: LevellingState, next?: LevellingState): boolean {
  if (prev?.autoLevel !== next?.autoLevel) {
    return false;
  }
  if (prev?.lowBound !== next?.lowBound) {
    return false;
  }
  if (prev?.highBound !== next?.highBound) {
    return false;
  }
  return true;
}

export default function ImageLeveling({ imagerIdentifier, style = {} }: Props) {
  var histogramData = useTypedSelector((state) => getHistogramData(state));
  var levellingState = useTypedSelector(
    (state) => getModuleStateById(state)[imagerIdentifier]?.imager?.levelling,
    isLevellingStateEqual
  );

  if (levellingState == null) {
    throw new Error("Levelling state is null");
  }

  const updateLeveling = useCallback(
    (value: readonly number[]) => {
      ImagerApi.setLeveling(imagerIdentifier, value[0], value[1]);
    },
    [imagerIdentifier]
  );

  const onClickAutoLevel = useCallback(() => {
    ImagerApi.autoLeveling(imagerIdentifier);
  }, [imagerIdentifier]);

  const onClickResetLevel = useCallback(() => {
    updateLeveling([0, 255]);
  }, [updateLeveling]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "8px 40px 8px 40px",
      }}
    >
      <Histogram
        histogramValues={histogramData.histogram}
        boundary={{ min: levellingState.lowBound, max: levellingState.highBound }}
        style={{
          padding: "0px 12px 0px 12px",
        }}
      />

      <ImageLevelingSlider
        disabled={levellingState.autoLevel}
        values={[levellingState.lowBound, levellingState.highBound]}
        min={0}
        max={255}
        onValuesChanged={updateLeveling}
        step={1}
        style={{
          margin: "10px 0px 10px 0px",
        }}
      />

      <ButtonInfoLevel
        disabled={levellingState.autoLevel}
        value={{ min: levellingState.lowBound, max: levellingState.highBound }}
        onValuesChanged={(values) => updateLeveling([values.min, values.max])}
        onClickAuto={onClickAutoLevel}
        onClickReset={onClickResetLevel}
        autoLevel={levellingState.autoLevel}
        style={{
          height: 34,
        }}
      />
    </div>
  );
}
